import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Skeleton,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
} from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  MdDelete,
  MdEdit,
  MdAdd,
  MdSearch,
  MdVisibility,
  MdShoppingBag,
  MdAttachMoney,
  MdCheckCircle,
  MdPendingActions,
  MdCancel,
  MdGridView,
  MdViewList,
  MdPhone,
  MdRefresh,
  MdClear,
  MdReceiptLong,
  MdLocalShipping,
  MdPayment,
  MdPictureAsPdf,
  MdFileUpload,
  MdFileDownload,
  MdPerson,
  MdAssignmentInd,
  MdLocalOffer,
  MdEvent,
  MdLocationOn,
  MdCameraAlt,
  MdLaunch,
  MdPhotoLibrary,
} from "react-icons/md";
import { createOrder, deleteOrder, getAllOrders, updateOrder, startWorkOrder, completeWorkOrder } from "../apis/orderApi";
import { getAllEmployees } from "../apis/employeeApi";
import { getAllservices } from "../apis/servicesApi";
import { getAllTimeslots } from "../apis/timeslotApi";
import toast from "react-hot-toast";
import { brand } from "../theme";
import { useAuth } from "../context/AuthProvider";

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) {
    return path;
  }
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  if (cleanPath.startsWith("uploads/")) {
    return `https://skishore.in/api/${cleanPath}`;
  }
  if (!cleanPath.startsWith("api/")) {
    return `https://skishore.in/api/${cleanPath}`;
  }
  return `https://skishore.in/${cleanPath}`;
};

const safeText = (val, fallback = "") => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") return String(val);
  if (Array.isArray(val)) {
    return val.map((v) => safeText(v)).filter(Boolean).join(", ");
  }
  if (typeof val === "object") {
    return val.name || val.title || val.label || val.username || val.full_name || val.address || fallback;
  }
  return fallback;
};

const formatTime12 = (timeStr) => {
  if (!timeStr) return "";
  const parts = String(timeStr).split(":");
  if (parts.length < 2) return String(timeStr);
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${hours}:${minutes} ${ampm}`;
};

const formatServiceText = (services, servicesMap = {}) => {
  if (!services) return "Work Order Service";
  if (typeof services === "number" || (typeof services === "string" && !isNaN(services) && services.trim() !== "")) {
    const sid = String(services).trim();
    if (servicesMap[sid]) return servicesMap[sid];
  }
  if (typeof services === "string") {
    const sid = services.trim();
    if (servicesMap[sid]) return servicesMap[sid];

    if (sid.startsWith("[")) {
      try {
        const parsed = JSON.parse(sid);
        if (Array.isArray(parsed)) {
          const names = parsed.map((s) => {
            if (typeof s === "object") return s.name || s.service_name || s.title;
            if (servicesMap[String(s)]) return servicesMap[String(s)];
            return isNaN(s) ? String(s) : `Service #${s}`;
          }).filter(Boolean);
          if (names.length > 0) return names.join(", ");
        }
      } catch (e) {
        // Ignore invalid JSON strings
      }
    }
    return sid.startsWith("uploads") ? "Work Service" : sid;
  }
  if (Array.isArray(services)) {
    const names = services.map((s) => {
      if (typeof s === "object") return s.name || s.service_name || s.title;
      if (servicesMap[String(s)]) return servicesMap[String(s)];
      return isNaN(s) ? String(s) : `Service #${s}`;
    }).filter(Boolean);
    return names.length > 0 ? names.join(", ") : "Work Service";
  }
  if (typeof services === "object") return services.name || services.service_name || services.title || "Work Service";
  return String(services);
};

const formatScheduleSlot = (order, timeslotsMap = {}) => {
  const date = order?.delivery_date || order?.date || "Not Scheduled";
  const slotId = order?.timeslot_id || order?.timeslot;
  let slotText = null;

  if (slotId && timeslotsMap[String(slotId)]) {
    slotText = timeslotsMap[String(slotId)];
  } else if (order?.timeslot && typeof order.timeslot === "string" && isNaN(order.timeslot)) {
    slotText = order.timeslot;
  } else if (order?.time_slot) {
    slotText = order.time_slot;
  } else if (slotId) {
    slotText = `Slot #${slotId}`;
  }

  if (slotText) return `${date} • ${slotText}`;
  return date;
};

const statusChipStyle = (status) => {
  const s = String(status || "pending").toLowerCase();
  if (s === "confirmed" || s === "delivered" || s === "completed") {
    return { bg: `${brand.success}1E`, color: brand.success, icon: <MdCheckCircle size={14} /> };
  }
  if (s === "assigned" || s === "in_progress") {
    return { bg: "#3B82F61E", color: "#3B82F6", icon: <MdLocalShipping size={14} /> };
  }
  if (s === "refunded") {
    return { bg: "#8B5CF61E", color: "#8B5CF6", icon: <MdReceiptLong size={14} /> };
  }
  if (s === "cancelled" || s === "rejected") {
    return { bg: `${brand.danger}1E`, color: brand.danger, icon: <MdCancel size={14} /> };
  }
  return { bg: `${brand.brass}20`, color: "#B4800A", icon: <MdPendingActions size={14} /> };
};

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: `${brand.brass}66`,
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || brand.brass}18`,
        color: accent || brand.ink950,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: "1.8rem",
          color: brand.ink950,
          lineHeight: 1.1,
        }}
      >
        {count}
      </Typography>
      <Typography sx={{ color: brand.slate, fontSize: "0.82rem", fontWeight: 600 }} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const Orders = () => {
  const { role, user: authUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("all"); // 'all' | 'today' | 'this_week' | 'this_month'
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [currentOrder, setCurrentOrder] = useState(null);

  const [servicesList, setServicesList] = useState([]);
  const [timeslotsList, setTimeslotsList] = useState([]);

  const [newOrder, setNewOrder] = useState({
    user_id: "",
    user_name: "",
    mobile: "",
    address: "",
    landmark: "",
    street: "",
    delivery_date: "",
    services: "",
    service_id: "",
    timeslot_id: "1",
    status: "confirmed",
    notes: "",
    sub_total: "",
    tax: "0",
    discount_amount: "0",
    coupon_code: "",
    total: "",
    payment_method: "COD",
    payment_status: "pending",
    transaction_id: "",
    assigned_person: "",
    cancellation_reason: "",
  });

  // Assign Staff & Employee State
  const [employees, setEmployees] = useState([]);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [assignOrder, setAssignOrder] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [staffFilter, setStaffFilter] = useState("all");

  // Work Tracking & Photo / GPS Modal State
  const [openWorkModal, setOpenWorkModal] = useState(false);
  const [workModalType, setWorkModalType] = useState("start"); // "start" or "complete"
  const [workOrder, setWorkOrder] = useState(null);
  const [workPhoto, setWorkPhoto] = useState(null);
  const [workLat, setWorkLat] = useState("");
  const [workLng, setWorkLng] = useState("");
  const [workAddress, setWorkAddress] = useState("");
  const [fetchingGps, setFetchingGps] = useState(false);
  const [submittingWork, setSubmittingWork] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const handleFetchGps = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    setFetchingGps(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setWorkLat(position.coords.latitude.toFixed(6));
        setWorkLng(position.coords.longitude.toFixed(6));
        setFetchingGps(false);
        toast.success("Captured GPS location!");
      },
      (error) => {
        console.error("GPS error:", error);
        setFetchingGps(false);
        toast.error("Failed to capture GPS. You can enter coordinates manually.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleOpenWorkModal = (order, type = "start") => {
    setWorkOrder(order);
    setWorkModalType(type);
    setWorkPhoto(null);
    setWorkLat(type === "start" ? order.start_latitude || "" : order.end_latitude || "");
    setWorkLng(type === "start" ? order.start_longitude || "" : order.end_longitude || "");
    setWorkAddress(type === "start" ? order.start_address || "" : order.end_address || "");
    setOpenWorkModal(true);
    if (navigator.geolocation && !order.start_latitude) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setWorkLat(pos.coords.latitude.toFixed(6));
          setWorkLng(pos.coords.longitude.toFixed(6));
        },
        () => {
          // Ignore geolocation error
        },
        { enableHighAccuracy: true }
      );
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setWorkPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitWork = async () => {
    if (!workOrder) return;
    const existingPhoto = workModalType === "start" ? workOrder.start_image : workOrder.end_image;
    if (!workPhoto && !existingPhoto) {
      toast.error("Please upload or capture a photo proof");
      return;
    }
    setSubmittingWork(true);
    try {
      const payload = {
        latitude: workLat,
        longitude: workLng,
        address: workAddress,
      };

      if (workPhoto) {
        payload[workModalType === "start" ? "start_image" : "end_image"] = workPhoto;
      }

      if (workModalType === "start") {
        await startWorkOrder(workOrder.id, payload);
        toast.success(`Started work on Order #${workOrder.id}`);
      } else {
        await completeWorkOrder(workOrder.id, payload);
        toast.success(`Completed work on Order #${workOrder.id}`);
      }

      setOpenWorkModal(false);
      await fetchOrders();
      if (selectedOrder && selectedOrder.id === workOrder.id) {
        const updated = await getAllOrders();
        const found = Array.isArray(updated) ? updated.find((o) => o.id === workOrder.id) : null;
        if (found) setSelectedOrder(found);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save work details");
    } finally {
      setSubmittingWork(false);
    }
  };

  const loggedInUser = useMemo(() => {
    if (authUser) return authUser;
    try {
      return JSON.parse(localStorage.getItem("user") || localStorage.getItem("employee") || localStorage.getItem("admin") || "{}");
    } catch {
      return {};
    }
  }, [authUser]);

  const userRole = (role || loggedInUser?.role || "").toLowerCase();
  const isVendor = userRole === "vendor";

  const [servicesMap, setServicesMap] = useState({});
  const [timeslotsMap, setTimeslotsMap] = useState({});

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await getAllOrders();
      setOrders(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.error("Failed to load order records");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLookups = async () => {
    try {
      const sRes = await getAllservices();
      const sMap = {};
      const sList = Array.isArray(sRes) ? sRes : (sRes?.data || []);
      sList.forEach((s) => {
        if (s.id) sMap[String(s.id)] = s.name || s.title || s.service_name;
      });
      setServicesMap(sMap);
      setServicesList(sList);
    } catch (e) {
      // Ignore services lookup failure
    }

    try {
      const tRes = await getAllTimeslots();
      const tMap = {};
      const tList = Array.isArray(tRes) ? tRes : (tRes?.data || []);
      tList.forEach((t) => {
        if (t.id) {
          const formatted = t.start_time ? formatTime12(t.start_time) : (t.slot_name || `Slot #${t.id}`);
          tMap[String(t.id)] = formatted;
        }
      });
      setTimeslotsMap(tMap);
      setTimeslotsList(tList);
    } catch (e) {
      // Ignore timeslots lookup failure
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchLookups();
    getAllEmployees().then((res) => {
      setEmployees(Array.isArray(res) ? res : []);
    });
  }, []);

  /* ------------------ PDF & EXPORT HANDLERS ------------------ */

  const handleExportPDF = (order) => {
    try {
      const doc = new jsPDF();

      // Branded Header
      doc.setFillColor(11, 10, 61);
      doc.rect(0, 0, 210, 35, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text("OurGenie Services", 14, 20);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(201, 162, 39);
      doc.text("Official Tax Invoice / Service Ticket", 14, 27);

      // Invoice info
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      doc.text(`Invoice No: OG-ORD-${order.id || order._id || "000"}`, 135, 15);
      doc.text(`Date: ${order.delivery_date || new Date().toISOString().split("T")[0]}`, 135, 22);
      doc.text(`Payment: ${String(order.payment_method || "COD").toUpperCase()}`, 135, 29);

      // Customer & Order Metadata
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(11, 10, 61);
      doc.text("Customer & Delivery Details", 14, 45);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(60, 60, 60);

      doc.text(`Customer Name: ${order.user_name || "N/A"}`, 14, 52);
      doc.text(`Mobile Contact: ${order.mobile || "N/A"}`, 14, 58);
      doc.text(`Delivery Address: ${order.address || "N/A"}`, 14, 64);
      if (order.landmark) doc.text(`Landmark: ${order.landmark}, ${order.street || ""}`, 14, 70);

      doc.text(`Assigned Staff: ${order.assigned_person || "Unassigned"}`, 120, 52);
      doc.text(`Order Status: ${String(order.status || "Pending").toUpperCase()}`, 120, 58);
      doc.text(`Payment Status: ${String(order.payment_status || "Pending").toUpperCase()}`, 120, 64);
      if (order.transaction_id) doc.text(`Transaction ID: ${order.transaction_id}`, 120, 70);

      // Items Table
      const sub = Number(order.sub_total || order.total || 0);
      const tx = Number(order.tax || 0);
      const disc = Number(order.discount_amount || 0);
      const tot = Number(order.total || sub + tx - disc);

      const tableData = [
        [
          formatServiceText(order.services, servicesMap),
          formatScheduleSlot(order, timeslotsMap),
          `₹${sub.toFixed(2)}`,
          `₹${tx.toFixed(2)}`,
          `₹${disc.toFixed(2)}`,
          `₹${tot.toFixed(2)}`,
        ],
      ];

      autoTable(doc, {
        startY: 78,
        head: [["Service Description", "Delivery Date", "Subtotal", "Tax", "Discount", "Total Amount"]],
        body: tableData,
        headStyles: { fillColor: [11, 10, 61], textColor: [255, 255, 255], fontStyle: "bold" },
        styles: { fontSize: 9, cellPadding: 4 },
      });

      const finalY = doc.lastAutoTable.finalY + 12;

      if (order.coupon_code) {
        doc.setFont("helvetica", "bold");
        doc.setTextColor(16, 185, 129);
        doc.text(`Applied Coupon: ${order.coupon_code} (Saved ₹${disc})`, 14, finalY);
      }

      if (order.cancellation_reason) {
        doc.setTextColor(220, 38, 38);
        doc.setFont("helvetica", "bold");
        doc.text(`Cancellation Reason: ${order.cancellation_reason}`, 14, finalY + 6);
      } else if (order.notes) {
        doc.setTextColor(80, 80, 80);
        doc.setFont("helvetica", "italic");
        doc.text(`Instructions: ${order.notes}`, 14, finalY + 6);
      }

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(150, 150, 150);
      doc.text("OurGenie Services Command Center • Automated Invoice • support@ourgenie.com", 14, 285);

      doc.save(`Invoice_OG_ORD_${order.id || "000"}.pdf`);
      toast.success("PDF Tax Invoice downloaded!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate PDF Invoice");
    }
  };

  const handleExportExcel = async (ordersList) => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Orders");

      worksheet.columns = [
        { header: "Order ID", key: "id", width: 12 },
        { header: "Customer Name", key: "user_name", width: 22 },
        { header: "Mobile", key: "mobile", width: 16 },
        { header: "Delivery Date", key: "delivery_date", width: 16 },
        { header: "Services", key: "services", width: 30 },
        { header: "Assigned Staff", key: "assigned_person", width: 20 },
        { header: "Status", key: "status", width: 15 },
        { header: "Payment Method", key: "payment_method", width: 16 },
        { header: "Payment Status", key: "payment_status", width: 16 },
        { header: "Subtotal (₹)", key: "sub_total", width: 15 },
        { header: "Tax (₹)", key: "tax", width: 12 },
        { header: "Discount (₹)", key: "discount_amount", width: 14 },
        { header: "Total Amount (₹)", key: "total", width: 16 },
        { header: "Address", key: "address", width: 30 },
        { header: "Cancellation Reason", key: "cancellation_reason", width: 25 },
      ];

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: "FFFFFF" } };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "0B0A3D" },
      };

      ordersList.forEach((o) => {
        worksheet.addRow({
          id: o.id || o._id,
          user_name: o.user_name || o.customer_name || "",
          mobile: o.mobile || "",
          delivery_date: formatScheduleSlot(o, timeslotsMap),
          services: formatServiceText(o.services, servicesMap),
          assigned_person: o.assigned_person || "Unassigned",
          status: String(o.status || "pending").toUpperCase(),
          payment_method: o.payment_method || "",
          payment_status: String(o.payment_status || "pending").toUpperCase(),
          sub_total: Number(o.sub_total || 0),
          tax: Number(o.tax || 0),
          discount_amount: Number(o.discount_amount || 0),
          total: Number(o.total || 0),
          address: o.address || "",
          cancellation_reason: o.cancellation_reason || "",
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `orders_report_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success(`Exported ${ordersList.length} orders to Excel!`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to export Excel report");
    }
  };

  /* ------------------ STATS & FILTERING ------------------ */

  const isOrderForVendor = useCallback(
    (order) => {
      if (!isVendor) return true;
      const myName = (loggedInUser?.name || "").trim().toLowerCase();
      const myEmail = (loggedInUser?.email || "").trim().toLowerCase();
      const myId = String(loggedInUser?.id || "");

      const assigned = String(order?.assigned_person || "").trim().toLowerCase();
      const vendorName = String(order?.vendor || order?.vendor_name || "").trim().toLowerCase();
      const createdBy = String(order?.created_by || "").trim().toLowerCase();
      const orderVendorId = String(order?.vendor_id || "");
      const orderUserId = String(order?.user_id || "");
      const orderEmail = String(order?.email || order?.vendor_email || "").trim().toLowerCase();

      return Boolean(
        (myName && assigned === myName) ||
        (myName && vendorName === myName) ||
        (myName && createdBy === myName) ||
        (myId && orderVendorId === myId) ||
        (myId && orderUserId === myId) ||
        (myEmail && orderEmail === myEmail)
      );
    },
    [isVendor, loggedInUser]
  );

  const vendorScopedOrders = useMemo(() => {
    if (!isVendor) return orders;
    return orders.filter(isOrderForVendor);
  }, [orders, isVendor, isOrderForVendor]);

  const stats = useMemo(() => {
    let totalRevenue = 0;
    let inProgressCount = 0;
    let completedCount = 0;
    let assignedCount = 0;

    const targetOrders = isVendor ? vendorScopedOrders : orders;

    targetOrders.forEach((o) => {
      const amt = Number(o.total || o.amount || 0);
      if (!isNaN(amt)) totalRevenue += amt;

      const st = String(o.status || "").toLowerCase();
      if (st === "completed" || st === "delivered" || o.end_image) {
        completedCount += 1;
      } else if (st === "in_progress" || o.start_image) {
        inProgressCount += 1;
      } else if (o.assigned_person || st === "assigned") {
        assignedCount += 1;
      }
    });

    return {
      totalOrders: targetOrders.length,
      totalRevenue: totalRevenue.toLocaleString("en-IN"),
      inProgressCount,
      completedCount,
      assignedCount,
    };
  }, [orders, isVendor, vendorScopedOrders]);

  const filteredOrders = useMemo(() => {
    const todayStr = new Date().toISOString().split("T")[0];
    const baseList = isVendor ? vendorScopedOrders : orders;

    return baseList.filter((order) => {
      // Status filter
      const st = String(order.status || "pending").toLowerCase();
      if (statusFilter !== "all" && st !== statusFilter) return false;

      // Staff filter (only for admin)
      if (!isVendor && staffFilter !== "all" && String(order.assigned_person || "").toLowerCase() !== staffFilter.toLowerCase()) {
        return false;
      }

      // If logged in as staff employee (not vendor), auto-filter to only their assigned orders
      if (userRole === "employee" || userRole === "staff") {
        if (loggedInUser?.name && String(order.assigned_person || "").toLowerCase() !== loggedInUser.name.toLowerCase()) {
          return false;
        }
      }

      // Date preset filter
      const orderDate = order.delivery_date || (order.created_at ? order.created_at.split("T")[0] : "");
      if (datePreset === "today" && orderDate !== todayStr) return false;
      if (datePreset === "this_week") {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        const weekAgo = d.toISOString().split("T")[0];
        if (orderDate < weekAgo) return false;
      }
      if (datePreset === "this_month") {
        const monthStr = new Date().toISOString().slice(0, 7);
        if (!orderDate.startsWith(monthStr)) return false;
      }

      // Custom date range
      if (startDate && orderDate < startDate) return false;
      if (endDate && orderDate > endDate) return false;

      // Search term
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLowerCase();

      const idMatch = String(order.id || order._id || "").toLowerCase().includes(q);
      const nameMatch = String(order.user_name || order.customer_name || "").toLowerCase().includes(q);
      const mobileMatch = String(order.mobile || "").toLowerCase().includes(q);
      const serviceMatch = String(formatServiceText(order.services, servicesMap)).toLowerCase().includes(q);
      const scheduleMatch = String(formatScheduleSlot(order, timeslotsMap)).toLowerCase().includes(q);
      const addressMatch = String(order.address || "").toLowerCase().includes(q);
      const staffMatch = String(order.assigned_person || "").toLowerCase().includes(q);
      const statusMatch = st.includes(q);

      return idMatch || nameMatch || mobileMatch || serviceMatch || scheduleMatch || addressMatch || staffMatch || statusMatch;
    });
  }, [orders, isVendor, vendorScopedOrders, statusFilter, staffFilter, userRole, loggedInUser, datePreset, startDate, endDate, searchTerm, servicesMap, timeslotsMap]);

  const paginatedOrders = useMemo(() => {
    return filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredOrders, page, rowsPerPage]);

  // Handlers
  const handleOpenAssign = (order) => {
    setAssignOrder(order);
    setSelectedStaff(order.assigned_person || "");
    setOpenAssignModal(true);
  };

  const handleSaveAssign = async () => {
    if (!assignOrder) return;
    try {
      const updatedData = {
        ...assignOrder,
        assigned_person: selectedStaff,
        status: (assignOrder.status === "pending" && selectedStaff) ? "assigned" : assignOrder.status,
      };
      await updateOrder(assignOrder.id, updatedData);
      toast.success(`Order #${assignOrder.id} assigned to ${selectedStaff || "Unassigned"}`);
      setOpenAssignModal(false);
      setAssignOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error("Failed to update staff assignment");
    }
  };

  const handleEdit = (order) => {
    setCurrentOrder({ ...order });
    setOpenEditDialog(true);
  };

  const handleDelete = (id) => {
    setConfirmDelete({ open: true, id });
  };

  const handleSubmitEdit = async () => {
    try {
      await updateOrder(currentOrder);
      setOrders((prev) => prev.map((o) => (o.id === currentOrder.id ? currentOrder : o)));
      setOpenEditDialog(false);
      toast.success("Order updated successfully");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to update order");
    }
  };

  const handleAdd = () => {
    setNewOrder({
      user_id: isVendor ? (loggedInUser?.id || "") : "",
      user_name: "",
      mobile: "",
      address: "",
      landmark: "",
      street: "",
      delivery_date: new Date().toISOString().split("T")[0],
      services: "",
      service_id: "",
      timeslot_id: timeslotsList.length > 0 ? String(timeslotsList[0].id) : "1",
      status: "confirmed",
      notes: "",
      sub_total: "",
      tax: "0",
      discount_amount: "0",
      coupon_code: "",
      total: "",
      payment_method: "COD",
      payment_status: "pending",
      transaction_id: "",
      assigned_person: isVendor ? (loggedInUser?.name || "") : "",
      created_by: loggedInUser?.name || "Admin",
      vendor_id: isVendor ? (loggedInUser?.id || "") : "",
      cancellation_reason: "",
    });
    setOpenAddDialog(true);
  };

  const handleNewOrderChange = (e) => {
    const { name, value } = e.target;
    setNewOrder((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitAdd = async () => {
    if (!newOrder.user_name?.trim() || !newOrder.mobile?.trim()) {
      toast.error("Customer Name and Mobile Number are required");
      return;
    }
    if (!newOrder.service_id && !newOrder.services?.trim()) {
      toast.error("Please choose a service for this order");
      return;
    }

    try {
      const payload = {
        ...newOrder,
        assigned_person: isVendor ? (loggedInUser?.name || newOrder.assigned_person) : newOrder.assigned_person,
        vendor: isVendor ? (loggedInUser?.name || "") : (newOrder.vendor || ""),
        vendor_id: isVendor ? (loggedInUser?.id || "") : (newOrder.vendor_id || ""),
        created_by: loggedInUser?.name || "Admin",
        services: newOrder.service_id ? String(newOrder.service_id) : (newOrder.services || "Work Service"),
      };

      const added = await createOrder(payload);
      setOrders((prev) => [added || payload, ...prev]);
      setOpenAddDialog(false);
      toast.success("Order created successfully");
      await fetchOrders();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to create order");
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleView = (order) => setSelectedOrder(order);
  const handleCloseView = () => setSelectedOrder(null);

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h3"
              sx={{
                color: brand.ink950,
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              {(loggedInUser?.role || "").toLowerCase() === "vendor" ? "My Assigned Work Orders" : "Order Operations"}
            </Typography>
            <Chip
              label={`${filteredOrders.length} Orders`}
              size="small"
              sx={{
                bgcolor: (loggedInUser?.role || "").toLowerCase() === "vendor" ? "#8B5CF622" : brand.brassSoft,
                color: (loggedInUser?.role || "").toLowerCase() === "vendor" ? "#6D28D9" : brand.ink950,
                fontWeight: 700,
              }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            {(loggedInUser?.role || "").toLowerCase() === "vendor"
              ? "Service orders assigned to your vendor account for execution and progress tracking"
              : "Track order fulfillments, payment transactions, and customer delivery invoices"}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload order records">
            <IconButton
              onClick={fetchOrders}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          <Button
            variant="outlined"
            onClick={() => handleExportExcel(filteredOrders)}
            startIcon={<MdFileDownload size={20} />}
            sx={{
              px: 2.2,
              py: 1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              borderColor: brand.ink950,
              color: brand.ink950,
              "&:hover": { bgcolor: `${brand.ink950}0B`, borderColor: brand.ink950 },
            }}
          >
            Export Excel
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleAdd}
            startIcon={<MdAdd size={20} />}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              boxShadow: "0 8px 20px -6px rgba(201,162,39,0.5)",
            }}
          >
            Create New Order
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards Grid ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdShoppingBag size={24} />} label="Total Placed Orders" count={stats.totalOrders} accent="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdAttachMoney size={24} />}
            label="Total Revenue Volume"
            count={`₹${stats.totalRevenue}`}
            accent={brand.brass}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdCameraAlt size={24} />}
            label="Work Started (In Progress)"
            count={stats.inProgressCount}
            accent="#7C3AED"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdCheckCircle size={24} />}
            label="Completed Jobs with Proof"
            count={stats.completedCount}
            accent="#10B981"
          />
        </Grid>
      </Grid>

      {/* ── Filter Controls Bar ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
        >
          <Box
            display="flex"
            flexDirection={{ xs: "column", lg: "row" }}
            alignItems={{ xs: "stretch", lg: "center" }}
            gap={1.5}
            flexWrap="wrap"
            justifyContent="space-between"
          >
            {/* Search Field */}
            <TextField
              placeholder="Search by Order ID, Customer, Phone, Staff..."
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              sx={{
                width: { xs: "100%", md: 280 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  bgcolor: `${brand.slateLight}15`,
                  fontSize: "0.85rem",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdSearch color={brand.slate} size={20} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <MdClear size={16} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* Staff Member Filter (Admin only) */}
            {!isVendor && (
              <FormControl size="small" sx={{ minWidth: 170, width: { xs: "100%", lg: "auto" } }}>
                <Select
                  value={staffFilter}
                  onChange={(e) => {
                    setStaffFilter(e.target.value);
                    setPage(0);
                  }}
                  displayEmpty
                  sx={{
                    borderRadius: "14px",
                    bgcolor: `${brand.slateLight}15`,
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    "& .MuiSelect-select": { py: 1.05 },
                  }}
                >
                  <MenuItem value="all">👥 All Field Staff</MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.name}>
                      👤 {emp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Date Presets */}
            <ToggleButtonGroup
              value={datePreset}
              exclusive
              onChange={(_, v) => {
                if (v) {
                  setDatePreset(v);
                  if (v === "all") {
                    setStartDate("");
                    setEndDate("");
                  }
                }
              }}
              size="small"
              sx={{
                width: { xs: "100%", lg: "auto" },
                bgcolor: `${brand.slateLight}15`,
                p: 0.4,
                borderRadius: "12px",
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "9px",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: brand.slate,
                  "&.Mui-selected": {
                    bgcolor: brand.ink950,
                    color: brand.parchment,
                  },
                },
              }}
            >
              <ToggleButton value="all">All Time</ToggleButton>
              <ToggleButton value="today">Today</ToggleButton>
              <ToggleButton value="this_week">This Week</ToggleButton>
              <ToggleButton value="this_month">This Month</ToggleButton>
            </ToggleButtonGroup>

            {/* Custom Date Range Pickers */}
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ width: { xs: "100%", lg: "auto" } }}>
              <TextField
                type="date"
                size="small"
                label="From Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "calc(50% - 20px)", sm: 140 }, "& .MuiOutlinedInput-root": { borderRadius: "12px", fontSize: "0.8rem" } }}
              />
              <TextField
                type="date"
                size="small"
                label="To Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "calc(50% - 20px)", sm: 140 }, "& .MuiOutlinedInput-root": { borderRadius: "12px", fontSize: "0.8rem" } }}
              />
              {(startDate || endDate) && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                >
                  <MdClear size={18} />
                </IconButton>
              )}
            </Box>

            {/* View Switcher */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{
                width: { xs: "100%", lg: "auto" },
                bgcolor: `${brand.slateLight}20`,
                p: 0.4,
                borderRadius: "12px",
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "9px",
                  px: 1.5,
                  py: 0.5,
                  color: brand.slate,
                  "&.Mui-selected": {
                    bgcolor: brand.paper,
                    color: brand.ink950,
                    boxShadow: "0 2px 8px rgba(11,10,61,0.08)",
                  },
                },
              }}
            >
              <ToggleButton value="table">
                <MdViewList size={18} />
              </ToggleButton>
              <ToggleButton value="grid">
                <MdGridView size={18} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Status Filter Chips */}
          <Box display="flex" gap={1} flexWrap="wrap" pt={0.5} borderTop={`1px solid ${brand.slateLight}25`}>
            {["all", "pending", "confirmed", "assigned", "in_progress", "delivered", "cancelled", "refunded"].map((st) => (
              <Chip
                key={st}
                label={st === "all" ? "All Statuses" : st.replace("_", " ").toUpperCase()}
                onClick={() => {
                  setStatusFilter(st);
                  setPage(0);
                }}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  px: 0.5,
                  bgcolor: statusFilter === st ? brand.ink950 : `${brand.slateLight}25`,
                  color: statusFilter === st ? brand.parchment : brand.slate,
                  "&:hover": { bgcolor: statusFilter === st ? brand.ink800 : `${brand.slateLight}40` },
                }}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* ── Display: Table vs Grid ───────────────────────────────────────── */}
      {viewMode === "table" ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            border: `1px solid ${brand.slateLight}40`,
            bgcolor: brand.paper,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            "& .MuiTable-root": { minWidth: 1500 },
            "& .MuiTableCell-root": { whiteSpace: "nowrap" },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Customer & Contact</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Service Booked</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Scheduled Visit & Time Slot</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Delivery Address</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Payment & Status</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Order Status</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Field Staff & Progress</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Total (₹)</TableCell>
                <TableCell align="right" sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 10 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedOrders.length > 0 ? (
                paginatedOrders.map((order, index) => {
                  const chipInfo = statusChipStyle(order.status);
                  const custName = safeText(order.user_name || order.customer_name, "Guest Customer");
                  const addr = safeText(order.address, "Address not specified");
                  const serviceName = formatServiceText(order.services, servicesMap);
                  const scheduleText = formatScheduleSlot(order, timeslotsMap);

                  return (
                    <TableRow key={order.id || order._id || index} sx={{ "&:hover": { bgcolor: `${brand.brassSoft}60` } }}>
                      <TableCell
                        sx={{
                          color: brand.slate,
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontSize: "0.82rem",
                          fontWeight: 600,
                        }}
                      >
                        #{String(order.id || page * rowsPerPage + index + 1).padStart(3, "0")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: brand.ink950, fontSize: "0.9rem" }}>
                        <Box display="flex" flexDirection="column">
                          <Typography sx={{ fontWeight: 700, color: brand.ink950, fontSize: "0.88rem" }}>
                            {custName}
                          </Typography>
                          {order.mobile && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.2} color={brand.slate}>
                              <MdPhone size={12} />
                              <Typography sx={{ fontSize: "0.78rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                                {order.mobile}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<MdShoppingBag size={13} />}
                          label={serviceName}
                          size="small"
                          sx={{
                            bgcolor: "#EFF6FF",
                            color: "#1D4ED8",
                            fontWeight: 700,
                            fontSize: "0.74rem",
                            maxWidth: 190,
                            "& .MuiChip-icon": { color: "#1D4ED8" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.6}>
                          <MdEvent size={14} color={brand.brass} />
                          <Typography sx={{ color: brand.ink950, fontSize: "0.8rem", fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace' }}>
                            {scheduleText}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: brand.slate, fontSize: "0.8rem", maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {addr}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.4}>
                          <Chip
                            label={(order.payment_method || "Online").toUpperCase()}
                            size="small"
                            sx={{ bgcolor: `${brand.slateLight}30`, fontSize: "0.7rem", fontWeight: 700, alignSelf: "flex-start" }}
                          />
                          {order.payment_status && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                color: order.payment_status === "paid" ? "#059669" : (order.payment_status === "failed" ? "#DC2626" : "#D97706"),
                              }}
                            >
                              Status: {String(order.payment_status).toUpperCase()}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={chipInfo.icon}
                          label={order.status || "pending"}
                          size="small"
                          sx={{
                            textTransform: "capitalize",
                            bgcolor: chipInfo.bg,
                            color: chipInfo.color,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            "& .MuiChip-icon": { color: "inherit" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {order.status === "completed" || order.status === "delivered" || order.end_image ? (
                          <Box display="flex" flexDirection="column" gap={0.3}>
                            <Chip
                              icon={<MdCheckCircle size={13} />}
                              label={`Finished: ${order.assigned_person || "Staff"}`}
                              size="small"
                              onClick={() => handleView(order)}
                              sx={{
                                bgcolor: "#D1FAE5",
                                color: "#059669",
                                fontWeight: 800,
                                fontSize: "0.72rem",
                                cursor: "pointer",
                                "& .MuiChip-icon": { color: "#059669" },
                              }}
                            />
                            {order.end_time && (
                              <Typography variant="caption" sx={{ color: brand.slate, fontSize: "0.68rem" }}>
                                Ended: {new Date(order.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            )}
                          </Box>
                        ) : order.status === "in_progress" || order.start_image ? (
                          <Box display="flex" flexDirection="column" gap={0.3}>
                            <Chip
                              icon={<MdCameraAlt size={13} />}
                              label={`Started: ${order.assigned_person || "Staff"}`}
                              size="small"
                              onClick={() => handleView(order)}
                              sx={{
                                bgcolor: "#EDE9FE",
                                color: "#7C3AED",
                                fontWeight: 800,
                                fontSize: "0.72rem",
                                cursor: "pointer",
                                "& .MuiChip-icon": { color: "#7C3AED" },
                              }}
                            />
                            {order.start_time && (
                              <Typography variant="caption" sx={{ color: brand.slate, fontSize: "0.68rem" }}>
                                Started: {new Date(order.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            )}
                          </Box>
                        ) : order.assigned_person ? (
                          <Chip
                            icon={<MdPerson size={13} />}
                            label={`Assigned: ${order.assigned_person}`}
                            size="small"
                            onClick={() => handleOpenAssign(order)}
                            sx={{
                              bgcolor: "#DBEAFE",
                              color: "#2563EB",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              cursor: "pointer",
                              "& .MuiChip-icon": { color: "#2563EB" },
                            }}
                          />
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenAssign(order)}
                            startIcon={<MdAssignmentInd size={13} />}
                            sx={{
                              borderRadius: "10px",
                              fontSize: "0.72rem",
                              py: 0.2,
                              px: 1,
                              borderColor: `${brand.slateLight}80`,
                              color: brand.slate,
                              textTransform: "none",
                            }}
                          >
                            + Assign Staff
                          </Button>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: brand.ink950, fontSize: "0.92rem" }}>
                        ₹{Number(order.total || order.amount || 0).toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={0.8} justifyContent="flex-end">
                          {!isVendor && (
                            <MuiTooltip title="Assign Field Staff / Driver">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenAssign(order)}
                                sx={{ bgcolor: `${brand.brass}30`, color: brand.ink950 }}
                              >
                                <MdAssignmentInd size={16} />
                              </IconButton>
                            </MuiTooltip>
                          )}
                          <MuiTooltip title="View Order Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(order)}
                              sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                            >
                              <MdVisibility size={16} />
                            </IconButton>
                          </MuiTooltip>
                          <MuiTooltip title="Download PDF Tax Invoice">
                            <IconButton
                              size="small"
                              onClick={() => handleExportPDF(order)}
                              sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                            >
                              <MdPictureAsPdf size={16} />
                            </IconButton>
                          </MuiTooltip>
                          <MuiTooltip title="Edit Order Details">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(order)}
                              sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                            >
                              <MdEdit size={16} />
                            </IconButton>
                          </MuiTooltip>
                          <MuiTooltip title="Delete Order Record">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(order.id)}
                              sx={{ bgcolor: `${brand.danger}15`, color: brand.danger }}
                            >
                              <MdDelete size={16} />
                            </IconButton>
                          </MuiTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <MdReceiptLong size={40} color={brand.slateLight} />
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: brand.ink950,
                        mt: 1,
                      }}
                    >
                      No order records found
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
                      {searchTerm || statusFilter !== "all"
                        ? "Try adjusting your search criteria or filter."
                        : "Click 'Create New Order' to add your first transaction."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredOrders.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3} mb={3}>
          {paginatedOrders.map((order) => {
            const chipInfo = statusChipStyle(order.status);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "22px",
                    bgcolor: brand.paper,
                    border: `1px solid ${brand.slateLight}40`,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 18px 40px -15px rgba(11,10,61,0.12)",
                      borderColor: `${brand.brass}66`,
                    },
                  }}
                >
                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                      <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "0.82rem", fontWeight: 700, color: brand.slate }}>
                        #{order.id}
                      </Typography>
                      <Chip
                        icon={chipInfo.icon}
                        label={order.status || "pending"}
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          bgcolor: chipInfo.bg,
                          color: chipInfo.color,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          "& .MuiChip-icon": { color: "inherit" },
                        }}
                      />
                    </Box>

                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: brand.ink950 }} noWrap>
                      {safeText(order.user_name, "Guest Customer")}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1} mt={1} color={brand.slate}>
                      <MdPhone size={15} />
                      <Typography sx={{ fontSize: "0.82rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                        {order.mobile || "—"}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} mt={0.6} color={brand.slate}>
                      <MdAssignmentInd size={15} />
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: order.assigned_person ? brand.ink950 : brand.slate }}>
                        {order.assigned_person || "Unassigned Staff"}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="baseline" justifyContent="space-between" mt={2} pt={1.5} borderTop={`1px solid ${brand.slateLight}30`}>
                      <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 600 }}>Total Amount</Typography>
                      <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.3rem", color: brand.ink950 }}>
                        ₹{Number(order.total || order.amount || 0).toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" gap={1} justifyContent="flex-end" mt={2.5}>
                    <Button size="small" variant="contained" color="secondary" onClick={() => handleOpenAssign(order)} startIcon={<MdAssignmentInd />}>
                      Assign
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => handleView(order)} startIcon={<MdVisibility />}>
                      Invoice
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => handleEdit(order)} startIcon={<MdEdit />}>
                      Edit
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Dialog: Order Invoice Details ──────────────────────────────── */}
      <Dialog
        open={!!selectedOrder}
        onClose={handleCloseView}
        PaperProps={{ sx: { borderRadius: "24px", p: 1.5, width: "100%", maxWidth: 640 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: { xs: "1.15rem", sm: "1.4rem" }, display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: 1.5, flexWrap: "wrap" }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: "1 1 280px" }}>
            <MdReceiptLong color={brand.brass} size={24} />
            <Box component="span">Order Invoice & Fulfillment Details</Box>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap" sx={{ flex: "0 1 auto" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleExportPDF(selectedOrder)}
              startIcon={<MdPictureAsPdf />}
              sx={{ borderRadius: "10px", fontSize: "0.78rem", fontWeight: 700 }}
            >
              PDF Invoice
            </Button>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => {
                const ord = selectedOrder;
                handleCloseView();
                handleEdit(ord);
              }}
              startIcon={<MdEdit />}
              sx={{ borderRadius: "10px", fontSize: "0.78rem", fontWeight: 700 }}
            >
              Edit
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Box display="flex" flexDirection="column" gap={2} my={1}>
              {/* Reference Header */}
              <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor={`${brand.slateLight}15`} p={2} borderRadius="16px">
                <Box>
                  <Typography sx={{ fontSize: "0.72rem", color: brand.slate, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}>
                    Order Reference ID
                  </Typography>
                  <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontWeight: 800, fontSize: "1.2rem", color: brand.ink950 }}>
                    #{String(selectedOrder.id).padStart(4, "0")}
                  </Typography>
                  {selectedOrder.created_at && (
                    <Typography variant="caption" sx={{ color: brand.slate, fontSize: "0.72rem" }}>
                      Created: {new Date(selectedOrder.created_at).toLocaleString()}
                    </Typography>
                  )}
                </Box>
                <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                  <Chip
                    icon={statusChipStyle(selectedOrder.status).icon}
                    label={selectedOrder.status || "pending"}
                    sx={{
                      textTransform: "capitalize",
                      bgcolor: statusChipStyle(selectedOrder.status).bg,
                      color: statusChipStyle(selectedOrder.status).color,
                      fontWeight: 800,
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                  {selectedOrder.payment_status && (
                    <Chip
                      label={`Payment: ${String(selectedOrder.payment_status).toUpperCase()}`}
                      size="small"
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        bgcolor: selectedOrder.payment_status === "paid" ? "#D1FAE5" : (selectedOrder.payment_status === "failed" ? "#FEE2E2" : "#FEF3C7"),
                        color: selectedOrder.payment_status === "paid" ? "#059669" : (selectedOrder.payment_status === "failed" ? "#DC2626" : "#D97706"),
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Service & Schedule Details Card */}
              <Box border={`1px solid ${brand.slateLight}40`} p={2} borderRadius="16px" bgcolor="#FAFAFA">
                <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700, textTransform: "uppercase", mb: 1, letterSpacing: 0.5 }}>
                  📦 Booked Service & Delivery Schedule
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: "0.72rem", color: brand.slate, fontWeight: 600 }}>
                      Service Title / Item
                    </Typography>
                    <Chip
                      icon={<MdShoppingBag size={14} />}
                      label={formatServiceText(selectedOrder.services, servicesMap)}
                      sx={{
                        mt: 0.5,
                        bgcolor: "#EFF6FF",
                        color: "#1D4ED8",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        "& .MuiChip-icon": { color: "#1D4ED8" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: "0.72rem", color: brand.slate, fontWeight: 600 }}>
                      Scheduled Visit & Time Slot
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.8} mt={0.8}>
                      <MdEvent size={16} color={brand.brass} />
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: brand.ink950, fontFamily: '"IBM Plex Mono", monospace' }}>
                        {formatScheduleSlot(selectedOrder, timeslotsMap)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Customer & Location Card */}
              <Box border={`1px solid ${brand.slateLight}40`} p={2} borderRadius="16px" bgcolor="#FAFAFA">
                <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700, textTransform: "uppercase", mb: 1, letterSpacing: 0.5 }}>
                  👤 Customer & Delivery Address
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontWeight: 800, color: brand.ink950, fontSize: "0.95rem" }}>
                      {selectedOrder.user_name || selectedOrder.customer_name || "Guest Customer"}
                    </Typography>
                    {selectedOrder.mobile && (
                      <Box display="flex" alignItems="center" gap={0.8} mt={0.5}>
                        <MdPhone size={15} color={brand.slate} />
                        <Typography
                          component="a"
                          href={`tel:${selectedOrder.mobile}`}
                          sx={{ fontSize: "0.85rem", color: brand.ink950, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                        >
                          {selectedOrder.mobile}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" gap={0.8}>
                      <MdLocationOn color="#EF4444" size={18} style={{ marginTop: 2, flexShrink: 0 }} />
                      <Box>
                        <Typography sx={{ fontSize: "0.83rem", color: brand.ink950, lineHeight: 1.4, fontWeight: 600 }}>
                          {selectedOrder.address || "Address not provided"}
                        </Typography>
                        {(selectedOrder.landmark || selectedOrder.street) && (
                          <Typography sx={{ fontSize: "0.78rem", color: brand.slate, mt: 0.3 }}>
                            {[selectedOrder.landmark, selectedOrder.street].filter(Boolean).join(" • ")}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Financial Breakdown */}
              <Box bgcolor={`${brand.brassSoft}77`} p={2} borderRadius="16px">
                <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700, textTransform: "uppercase", mb: 1, letterSpacing: 0.5 }}>
                  💳 Financial Breakdown & Payment
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: "0.82rem", color: brand.slate }}>Service Subtotal</Typography>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                    ₹{Number(selectedOrder.sub_total || selectedOrder.total || 0).toLocaleString("en-IN")}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: "0.82rem", color: brand.slate }}>Tax Charges</Typography>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                    ₹{Number(selectedOrder.tax || 0).toLocaleString("en-IN")}
                  </Typography>
                </Box>
                {Number(selectedOrder.discount_amount) > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.5} color="success.main">
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                      Discount Coupon ({selectedOrder.coupon_code || "Applied"})
                    </Typography>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
                      -₹{Number(selectedOrder.discount_amount).toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: "0.82rem", color: brand.slate }}>Payment Method</Typography>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: brand.ink950 }}>
                    {(selectedOrder.payment_method || "Online / Cash").toUpperCase()}
                  </Typography>
                </Box>
                {selectedOrder.transaction_id && (
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography sx={{ fontSize: "0.82rem", color: brand.slate }}>Transaction ID</Typography>
                    <Typography sx={{ fontSize: "0.82rem", fontFamily: '"IBM Plex Mono", monospace', fontWeight: 600 }}>
                      {selectedOrder.transaction_id}
                    </Typography>
                  </Box>
                )}
                {selectedOrder.cancellation_reason && (
                  <Box display="flex" justifyContent="space-between" mt={1} pt={1} borderTop={`1px dashed ${brand.danger}40`} color="error.main">
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>Cancellation Reason</Typography>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                      {selectedOrder.cancellation_reason}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between" alignItems="baseline">
                  <Typography sx={{ fontWeight: 800, color: brand.ink950, fontSize: "1rem" }}>Grand Total Amount</Typography>
                  <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 800, fontSize: "1.5rem", color: brand.ink950 }}>
                    ₹{Number(selectedOrder.total || selectedOrder.amount || 0).toLocaleString("en-IN")}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              {/* Employee Work Tracking & Proof Card */}
              <Box bgcolor="#F8FAFC" border={`1px solid ${brand.slateLight}40`} p={2} borderRadius="16px">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <MdAssignmentInd size={20} color={brand.brass} />
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: brand.ink950 }}>
                      Employee Work Tracking & Proof
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    {selectedOrder.status !== "completed" && (
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        startIcon={<MdCameraAlt />}
                        onClick={() => handleOpenWorkModal(selectedOrder, selectedOrder.status === "in_progress" ? "complete" : "start")}
                        sx={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "none", borderRadius: "8px" }}
                      >
                        {selectedOrder.status === "in_progress" ? "Log Complete Work" : "Log Start Work"}
                      </Button>
                    )}
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  {/* Start Work Proof */}
                  <Grid item xs={12} sm={6}>
                    <Box bgcolor="#FFFFFF" p={1.5} borderRadius="12px" border="1px solid #E2E8F0">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Chip label="1. Start Work Proof" size="small" sx={{ bgcolor: "#E0F2FE", color: "#0369A1", fontWeight: 700, fontSize: "0.7rem" }} />
                      </Box>
                      {selectedOrder.start_time && (
                        <Typography sx={{ fontSize: "0.72rem", color: brand.slate, mb: 1 }}>
                          Started: {new Date(selectedOrder.start_time).toLocaleString()}
                        </Typography>
                      )}
                      {selectedOrder.start_image ? (
                        <Box
                          component="img"
                          src={getImageUrl(selectedOrder.start_image)}
                          alt="Start Work Photo"
                          onClick={() => setPreviewImage(getImageUrl(selectedOrder.start_image))}
                          sx={{
                            width: "100%",
                            height: 120,
                            objectFit: "cover",
                            borderRadius: "8px",
                            cursor: "pointer",
                            border: "1px solid #CBD5E1",
                            transition: "transform 0.2s",
                            "&:hover": { transform: "scale(1.02)" },
                          }}
                        />
                      ) : (
                        <Box height={100} display="flex" alignItems="center" justifyContent="center" bgcolor="#F1F5F9" borderRadius="8px">
                          <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>No Start Photo</Typography>
                        </Box>
                      )}
                      {(selectedOrder.start_latitude && selectedOrder.start_longitude) ? (
                        <Box mt={1}>
                          <Typography sx={{ fontSize: "0.72rem", color: brand.slate, display: "flex", alignItems: "center", gap: 0.5 }}>
                            <MdLocationOn color="#EF4444" size={14} /> {selectedOrder.start_latitude}, {selectedOrder.start_longitude}
                          </Typography>
                          <Button
                            size="small"
                            component="a"
                            href={`https://maps.google.com/?q=${selectedOrder.start_latitude},${selectedOrder.start_longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<MdLaunch size={12} />}
                            sx={{ p: 0, mt: 0.2, fontSize: "0.7rem", textTransform: "none", fontWeight: 700 }}
                          >
                            Open in Google Maps
                          </Button>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", mt: 1 }}>GPS pending</Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Complete Work Proof */}
                  <Grid item xs={12} sm={6}>
                    <Box bgcolor="#FFFFFF" p={1.5} borderRadius="12px" border="1px solid #E2E8F0">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Chip label="2. Complete Work Proof" size="small" sx={{ bgcolor: "#DCFCE7", color: "#15803D", fontWeight: 700, fontSize: "0.7rem" }} />
                      </Box>
                      {selectedOrder.end_time && (
                        <Typography sx={{ fontSize: "0.72rem", color: brand.slate, mb: 1 }}>
                          Completed: {new Date(selectedOrder.end_time).toLocaleString()}
                        </Typography>
                      )}
                      {selectedOrder.end_image ? (
                        <Box
                          component="img"
                          src={getImageUrl(selectedOrder.end_image)}
                          alt="End Work Photo"
                          onClick={() => setPreviewImage(getImageUrl(selectedOrder.end_image))}
                          sx={{
                            width: "100%",
                            height: 120,
                            objectFit: "cover",
                            borderRadius: "8px",
                            cursor: "pointer",
                            border: "1px solid #CBD5E1",
                            transition: "transform 0.2s",
                            "&:hover": { transform: "scale(1.02)" },
                          }}
                        />
                      ) : (
                        <Box height={100} display="flex" alignItems="center" justifyContent="center" bgcolor="#F1F5F9" borderRadius="8px">
                          <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>No Complete Photo</Typography>
                        </Box>
                      )}
                      {(selectedOrder.end_latitude && selectedOrder.end_longitude) ? (
                        <Box mt={1}>
                          <Typography sx={{ fontSize: "0.72rem", color: brand.slate, display: "flex", alignItems: "center", gap: 0.5 }}>
                            <MdLocationOn color="#EF4444" size={14} /> {selectedOrder.end_latitude}, {selectedOrder.end_longitude}
                          </Typography>
                          <Button
                            size="small"
                            component="a"
                            href={`https://maps.google.com/?q=${selectedOrder.end_latitude},${selectedOrder.end_longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<MdLaunch size={12} />}
                            sx={{ p: 0, mt: 0.2, fontSize: "0.7rem", textTransform: "none", fontWeight: 700 }}
                          >
                            Open in Google Maps
                          </Button>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", mt: 1 }}>GPS pending</Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={() => handleExportPDF(selectedOrder)}
            variant="outlined"
            startIcon={<MdPictureAsPdf />}
            sx={{ fontWeight: 700, borderRadius: "10px", borderColor: brand.ink950, color: brand.ink950 }}
          >
            Download PDF Tax Invoice
          </Button>
          <Button onClick={handleCloseView} variant="contained" color="secondary" sx={{ fontWeight: 700, borderRadius: "10px" }}>
            Close Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Edit Order ─────────────────────────────────────────── */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 540 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Edit Order Record
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  label="Customer Name"
                  value={currentOrder?.user_name || ""}
                  onChange={(e) => setCurrentOrder({ ...currentOrder, user_name: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Mobile Contact"
                  value={currentOrder?.mobile || ""}
                  onChange={(e) => setCurrentOrder({ ...currentOrder, mobile: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Order Status</InputLabel>
                  <Select
                    value={currentOrder?.status || "pending"}
                    label="Order Status"
                    onChange={(e) => setCurrentOrder({ ...currentOrder, status: e.target.value })}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="assigned">Assigned to Staff</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="delivered">Delivered / Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="refunded">Refunded</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Status</InputLabel>
                  <Select
                    value={currentOrder?.payment_status || "pending"}
                    label="Payment Status"
                    onChange={(e) => setCurrentOrder({ ...currentOrder, payment_status: e.target.value })}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="refunded">Refunded</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              label="Assigned Field Staff / Driver"
              placeholder="Technician or Driver Name"
              value={currentOrder?.assigned_person || ""}
              onChange={(e) => setCurrentOrder({ ...currentOrder, assigned_person: e.target.value })}
              fullWidth
            />

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  label="Subtotal (₹)"
                  type="number"
                  value={currentOrder?.sub_total || ""}
                  onChange={(e) => setCurrentOrder({ ...currentOrder, sub_total: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Total Amount (₹)"
                  type="number"
                  value={currentOrder?.total || currentOrder?.amount || ""}
                  onChange={(e) => setCurrentOrder({ ...currentOrder, total: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  label="Coupon Code"
                  value={currentOrder?.coupon_code || ""}
                  onChange={(e) => setCurrentOrder({ ...currentOrder, coupon_code: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Discount Amount (₹)"
                  type="number"
                  value={currentOrder?.discount_amount || ""}
                  onChange={(e) => setCurrentOrder({ ...currentOrder, discount_amount: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            {(currentOrder?.status === "cancelled" || currentOrder?.status === "refunded") && (
              <TextField
                label="Cancellation / Refund Reason"
                placeholder="Reason for cancellation..."
                value={currentOrder?.cancellation_reason || ""}
                onChange={(e) => setCurrentOrder({ ...currentOrder, cancellation_reason: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
            )}

            <TextField
              label="Delivery Address"
              value={currentOrder?.address || ""}
              onChange={(e) => setCurrentOrder({ ...currentOrder, address: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenEditDialog(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleSubmitEdit} variant="contained" color="secondary" sx={{ fontWeight: 700, borderRadius: "10px" }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Add Order ─────────────────────────────────────────── */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 540 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Create New Order Record
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  label="Customer Name"
                  name="user_name"
                  value={newOrder.user_name}
                  onChange={handleNewOrderChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Mobile Number"
                  name="mobile"
                  value={newOrder.mobile}
                  onChange={handleNewOrderChange}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  label="Delivery Date"
                  name="delivery_date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={newOrder.delivery_date}
                  onChange={handleNewOrderChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Order Status</InputLabel>
                  <Select name="status" value={newOrder.status} label="Order Status" onChange={handleNewOrderChange}>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="assigned">Assigned to Staff</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="delivered">Delivered / Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Service Chooser Dropdown */}
            <FormControl fullWidth required>
              <InputLabel id="order-service-select-label">Choose Service</InputLabel>
              <Select
                labelId="order-service-select-label"
                label="Choose Service"
                value={newOrder.service_id || ""}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const srv = servicesList.find((s) => String(s.id) === String(selectedId));
                  if (srv) {
                    const priceVal = srv.salePrice || srv.price || "";
                    setNewOrder((prev) => ({
                      ...prev,
                      service_id: String(srv.id),
                      services: srv.name || `Service #${srv.id}`,
                      sub_total: String(priceVal),
                      total: String(priceVal),
                    }));
                  } else {
                    setNewOrder((prev) => ({
                      ...prev,
                      service_id: "",
                    }));
                  }
                }}
              >
                <MenuItem value="">
                  <em>-- Choose a Service --</em>
                </MenuItem>
                {servicesList.map((srv) => (
                  <MenuItem key={srv.id} value={String(srv.id)}>
                    <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                      <Typography sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
                        {srv.name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.8rem", color: brand.brass, fontWeight: 700, ml: 2 }}>
                        ₹{Number(srv.salePrice || srv.price || 0).toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Time Slot Dropdown */}
            {timeslotsList.length > 0 && (
              <FormControl fullWidth>
                <InputLabel id="order-timeslot-select-label">Preferred Time Slot</InputLabel>
                <Select
                  labelId="order-timeslot-select-label"
                  label="Preferred Time Slot"
                  name="timeslot_id"
                  value={newOrder.timeslot_id || ""}
                  onChange={handleNewOrderChange}
                >
                  {timeslotsList.map((slot) => (
                    <MenuItem key={slot.id} value={String(slot.id)}>
                      {slot.start_time ? formatTime12(slot.start_time) : (slot.slot_name || `Slot #${slot.id}`)}
                      {slot.end_time ? ` - ${formatTime12(slot.end_time)}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Assignee: Vendor (Auto) or Staff (Admin) */}
            {isVendor ? (
              <Box
                p={1.5}
                borderRadius="14px"
                bgcolor={`${brand.brassSoft}70`}
                border={`1px solid ${brand.brass}44`}
                display="flex"
                alignItems="center"
                gap={1.5}
              >
                <Avatar sx={{ width: 34, height: 34, bgcolor: "#8B5CF6", fontSize: "0.82rem", fontWeight: 700 }}>
                  {loggedInUser?.name?.charAt(0)?.toUpperCase() || "V"}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: "0.72rem", color: brand.slate, fontWeight: 600, textTransform: "uppercase" }}>
                    Assigned Vendor Partner
                  </Typography>
                  <Typography sx={{ fontSize: "0.88rem", color: brand.ink950, fontWeight: 700 }}>
                    {loggedInUser?.name || "Vendor Partner"} {loggedInUser?.email ? `• ${loggedInUser.email}` : ""}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <TextField
                label="Assigned Field Staff / Driver"
                name="assigned_person"
                placeholder="Field Staff Name"
                value={newOrder.assigned_person}
                onChange={handleNewOrderChange}
                fullWidth
              />
            )}

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  label="Subtotal (₹)"
                  name="sub_total"
                  type="number"
                  value={newOrder.sub_total}
                  onChange={handleNewOrderChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Total Amount (₹)"
                  name="total"
                  type="number"
                  value={newOrder.total}
                  onChange={handleNewOrderChange}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  label="Coupon Code"
                  name="coupon_code"
                  value={newOrder.coupon_code}
                  onChange={handleNewOrderChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Discount Amount (₹)"
                  name="discount_amount"
                  type="number"
                  value={newOrder.discount_amount}
                  onChange={handleNewOrderChange}
                  fullWidth
                />
              </Grid>
            </Grid>

            <TextField
              label="Service Specifications & Notes"
              name="services"
              placeholder="Custom specifications or customer notes"
              value={newOrder.services}
              onChange={handleNewOrderChange}
              fullWidth
            />

            <TextField
              label="Shipping / Service Address"
              name="address"
              value={newOrder.address}
              onChange={handleNewOrderChange}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenAddDialog(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleSubmitAdd} variant="contained" color="secondary" sx={{ fontWeight: 700, borderRadius: "10px" }}>
            Create Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Assign Field Staff / Employee ─────────────────────────── */}
      <Dialog
        open={openAssignModal}
        onClose={() => setOpenAssignModal(false)}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 460 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.3rem" }}>
          Assign Vendor Partner / Field Staff
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Assigning work for <strong>Order #{assignOrder?.id}</strong> ({safeText(assignOrder?.user_name)})
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Select Assignee (Vendor or Staff)</InputLabel>
              <Select
                value={selectedStaff}
                label="Select Assignee (Vendor or Staff)"
                onChange={(e) => setSelectedStaff(e.target.value)}
              >
                <MenuItem value=""><em>-- Unassigned --</em></MenuItem>
                {employees.map((emp) => {
                  const isVendorEmp = (emp.role || "").toLowerCase() === "vendor";
                  return (
                    <MenuItem key={emp.id} value={emp.name}>
                      <Box display="flex" alignItems="center" gap={1} width="100%">
                        <span>{emp.name}</span>
                        <Chip
                          label={isVendorEmp ? "Vendor Partner" : (emp.role ? emp.role.toUpperCase() : "Staff")}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            bgcolor: isVendorEmp ? "#8B5CF622" : brand.brassSoft,
                            color: isVendorEmp ? "#6D28D9" : brand.ink950,
                          }}
                        />
                        {emp.mobile && <span style={{ color: brand.slate, fontSize: "0.75rem", marginLeft: "auto" }}>{emp.mobile}</span>}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              label="Or Type Custom Staff Name"
              placeholder="e.g. Alex Miller"
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value)}
              fullWidth
              helperText="Select an employee from the list or enter custom staff name."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenAssignModal(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleSaveAssign} variant="contained" color="secondary" sx={{ fontWeight: 700, borderRadius: "10px" }}>
            Assign & Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirm Delete ─────────────────────────────────────── */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, id: null })}
        PaperProps={{ sx: { borderRadius: "20px", p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700 }}>
          Delete Order Record?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: brand.slate, fontSize: "0.9rem" }}>
            Are you sure you want to delete this order record? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete({ open: false, id: null })} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              try {
                await deleteOrder(confirmDelete.id);
                setOrders((prev) => prev.filter((o) => o.id !== confirmDelete.id));
                toast.success("Order deleted successfully");
              } catch (e) {
                toast.error(e?.response?.data?.error || "Failed to delete order");
              }
              setConfirmDelete({ open: false, id: null });
            }}
            sx={{ fontWeight: 700 }}
          >
            Delete Record
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Log / Capture Work Photo & GPS ──────────────────────── */}
      <Dialog
        open={openWorkModal}
        onClose={() => setOpenWorkModal(false)}
        PaperProps={{ sx: { borderRadius: "24px", p: 1, width: "100%", maxWidth: 500 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.3rem" }}>
          {workModalType === "start" ? "Log Work Start Proof (Photo & GPS)" : "Log Work Completion Proof (Photo & GPS)"}
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} my={1}>
            <Typography variant="body2" color="text.secondary">
              Order #{workOrder?.id} — {safeText(workOrder?.user_name)} ({workOrder?.services || "Service"})
            </Typography>

            {/* Photo Selection / Camera Capture */}
            <Box>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, mb: 1, color: brand.ink950 }}>
                {workModalType === "start" ? "Start Work Photo Proof *" : "Completion Work Photo Proof *"}
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<MdPhotoLibrary />}
                fullWidth
                sx={{ borderRadius: "12px", py: 1.2, fontWeight: 700 }}
              >
                Upload / Capture Photo
                <input type="file" accept="image/*" capture="environment" hidden onChange={handlePhotoChange} />
              </Button>
              {workPhoto && (
                <Box mt={1.5} textAlign="center">
                  <Box
                    component="img"
                    src={workPhoto}
                    alt="Preview"
                    sx={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: "10px", border: "1px solid #CBD5E1" }}
                  />
                  <Typography sx={{ fontSize: "0.75rem", color: "success.main", fontWeight: 700, mt: 0.5 }}>
                    ✓ Photo ready to submit
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider />

            {/* GPS Location Capture */}
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: brand.ink950 }}>
                  GPS Location Coordinates
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  onClick={handleFetchGps}
                  disabled={fetchingGps}
                  startIcon={<MdLocationOn />}
                  sx={{ fontSize: "0.75rem", borderRadius: "8px", fontWeight: 700 }}
                >
                  {fetchingGps ? "Capturing GPS..." : "Get Live GPS"}
                </Button>
              </Box>

              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField
                    label="Latitude"
                    placeholder="e.g. 12.971598"
                    value={workLat}
                    onChange={(e) => setWorkLat(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Longitude"
                    placeholder="e.g. 77.594566"
                    value={workLng}
                    onChange={(e) => setWorkLng(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>

              <TextField
                label="Location Note / Address (Optional)"
                placeholder="e.g. Customer site location"
                value={workAddress}
                onChange={(e) => setWorkAddress(e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenWorkModal(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitWork}
            variant="contained"
            color="secondary"
            disabled={submittingWork}
            sx={{ fontWeight: 700, borderRadius: "10px" }}
          >
            {submittingWork ? "Saving..." : (workModalType === "start" ? "Confirm Work Start" : "Confirm Work Completion")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Full Size Photo Preview ─────────────────────────────── */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        PaperProps={{ sx: { borderRadius: "20px", p: 1, maxWidth: 600 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Captured Work Proof Photo
          <IconButton size="small" onClick={() => setPreviewImage(null)}>
            <MdClear />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Full Proof"
              sx={{ width: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: "12px" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Orders;
